import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Heart, Feather, Wind, Sparkles, Moon, Music, Flame, Leaf, 
  Brain, TreeDeciduous, Users, Award,
  Check, ArrowRight, Star, MessageCircle, Shield, ChevronDown, Quote
} from "lucide-react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Helmet } from "react-helmet";

const HERO_IMG = "https://images.unsplash.com/photo-1550927049-d07581451e3c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";
const FARMHOUSE_IMG = "https://images.unsplash.com/photo-1592212671488-700bb9a101e1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const MEDITATION_IMG = "https://images.unsplash.com/photo-1506126613408-eca07ce68773?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [founderSpots, setFounderSpots] = useState(100);
  const [openFaq, setOpenFaq] = useState(null);
  const [userReviews, setUserReviews] = useState([]);
  const [showFullLetter, setShowFullLetter] = useState(false);

  useEffect(() => {
    axios.get(`${API}/subscription/founder-status`).then(res => {
      setFounderSpots(res.data.remaining_founder_spots);
    }).catch(() => {});
    
    axios.get(`${API}/reviews/featured`).then(res => {
      setUserReviews(res.data.reviews || []);
    }).catch(() => {});
  }, []);

  const handleGetStarted = () => {
    if (user) {
      navigate(user.quiz_completed ? "/dashboard" : "/quiz");
    } else {
      navigate("/auth");
    }
  };

  const testimonials = [
    {
      quote: "This app helped me understand patterns I'd been repeating for 20 years. The shadow work sessions changed my relationship with myself.",
      name: "Sarah M.",
      location: "Melbourne"
    },
    {
      quote: "The Witness Circle connected me with someone who truly understands. We've never met, but our weekly check-ins keep me accountable.",
      name: "James K.",
      location: "Sydney"
    },
    {
      quote: "Spirit AI feels like talking to a wise friend at 3am. No judgment, just gentle guidance back to myself.",
      name: "Emily R.",
      location: "Brisbane"
    }
  ];

  const faqs = [
    {
      q: "Is this app only for women?",
      a: "No! While originally designed with women over 30 in mind, Authentic Self 365 is now gender-neutral and welcomes anyone seeking deeper self-understanding and growth."
    },
    {
      q: "What makes this different from other meditation apps?",
      a: "We go beyond surface-level wellness. Our platform combines Jungian psychology (shadow work), intergenerational healing (ancestral patterns), AI-powered pattern recognition, and peer accountability (Witness Circle) for transformational change."
    },
    {
      q: "What happens after the 3-day free trial?",
      a: "Your card is charged the plan amount after 3 days. You can cancel anytime before that with no charge. The first 100 paid subscribers get 25% off for life."
    },
    {
      q: "Is the Witness Circle anonymous?",
      a: "Yes! You're matched with a soul companion based on your journey stage. You communicate through soul names (like 'Dreaming Cloud' or 'Gentle Oak') – no personal details are shared."
    },
    {
      q: "Can I use the free version forever?",
      a: "Absolutely. Our free tier includes the State of Being Quiz, daily mantras, mood tracking, loving kindness practice, 7 Shadow Self sessions, 5 Ancestral Wisdom sessions, and more."
    }
  ];

  const features = [
    { testid: "feature-mood-tracker", icon: Flame, color: "text-amber-500", bg: "bg-amber-100", title: "Mood Tracker", desc: "Daily dandelion check-ins. Build streaks and unlock badges as you grow emotional awareness." },
    { testid: "feature-spirit", icon: Sparkles, color: "text-primary", bg: "bg-primary/10", title: "Spirit AI Companion", desc: "A wise AI guide for comfort, insight, and gentle guidance — whenever you need it." },
    { testid: "feature-journal", icon: Feather, color: "text-accent", bg: "bg-accent/15", title: "365 Journal Prompts", desc: "A year of thoughtful prompts to write your way to self-understanding and freedom." },
    { testid: "feature-kindness", icon: Heart, color: "text-rose-500", bg: "bg-rose-100", title: "Daily Loving Kindness", desc: "One kind act a day. Rewire your brain to notice the good in yourself and others." },
    { testid: "feature-ei", icon: Brain, color: "text-teal-600", bg: "bg-teal-100", title: "Emotional Intelligence", desc: "22 evidence-based modules to understand and work with your emotions." },
    { testid: "feature-habits", icon: Leaf, color: "text-green-600", bg: "bg-green-100", title: "Healthy Habits", desc: "20 modules on self-care and self-acceptance. Sustainable practices that honour you." },
    { testid: "feature-meditations", icon: Wind, color: "text-primary", bg: "bg-primary/10", title: "Guided Practices", desc: "Meditations, breathwork, and grounding for busy souls seeking stillness." },
    { testid: "feature-playlists", icon: Music, color: "text-primary", bg: "bg-primary/10", title: "9 Mood Playlists", desc: "Curated Spotify playlists for every emotional state — morning energy to creative flow." },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Dandelion Farm: House of Bloom | Authentic Self 365 — A Movement for Authentic Living</title>
        <meta name="description" content="A movement for authentic living. 365 days of guided self-discovery — shadow work, ancestral healing, AI-powered insights, and a community building the House of Bloom sanctuary." />
        <meta name="author" content="Ms J. C. Wallace, JVDH Lived Insights" />
        <meta name="keywords" content="self-discovery, personal growth, shadow work, emotional intelligence, mindfulness, meditation, journaling, authentic self, dandelion farm, house of bloom, JVDH Lived Insights" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Dandelion Farm: House of Bloom | Authentic Self 365" />
        <meta property="og:description" content="A movement for authentic living. Grow in the hard places — and bloom anyway. Every membership helps build the physical House of Bloom sanctuary." />
        <meta property="og:type" content="website" />
        
        {/* Schema.org Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Authentic Self 365 — Dandelion Farm: House of Bloom",
            "description": "A movement for authentic living combining Jungian shadow work, ancestral healing, AI-powered insights, and peer accountability.",
            "applicationCategory": "HealthApplication",
            "creator": {
              "@type": "Person",
              "name": "Ms J. C. Wallace",
              "jobTitle": "Lived-Experience Advocate & Author",
              "worksFor": {
                "@type": "Organization",
                "name": "JVDH Lived Insights"
              }
            },
            "offers": {
              "@type": "Offer",
            "price": "5.55",
            "priceCurrency": "USD"
            },
            "aggregateRating": userReviews.length > 0 ? {
              "@type": "AggregateRating",
              "ratingValue": (userReviews.reduce((sum, r) => sum + r.rating, 0) / userReviews.length).toFixed(1),
              "reviewCount": userReviews.length,
              "bestRating": "5"
            } : undefined
          })}
        </script>
      </Helmet>
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/70 backdrop-blur-md border-b border-stone-200/60">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center">
              <Heart className="w-5 h-5 text-primary" strokeWidth={1.5} />
            </div>
            <div className="leading-tight">
              <span className="font-heading text-xl font-semibold text-foreground block">Authentic Self 365</span>
              <span className="text-[10px] uppercase tracking-[0.18em] text-primary font-bold hidden sm:block">Dandelion Farm · House of Bloom</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {user ? (
              <Button
                onClick={() => navigate("/dashboard")}
                className="btn-primary"
                data-testid="dashboard-nav-btn"
              >
                My Journey
              </Button>
            ) : (
              <>
                <Button
                  variant="ghost"
                  onClick={() => navigate("/auth")}
                  className="text-foreground/70 hover:text-foreground"
                  data-testid="login-nav-btn"
                >
                  Sign In
                </Button>
                <Button
                  onClick={() => navigate("/auth")}
                  className="btn-primary"
                  data-testid="get-started-nav-btn"
                >
                  Start Free Trial
                </Button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* ============ SECTION 1: HERO ============ */}
      <section className="relative min-h-screen flex items-center">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('${HERO_IMG}')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/45 to-transparent" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 w-full pt-28 pb-20">
          <div className="max-w-2xl">
            <span className="uppercase text-xs tracking-[0.25em] text-amber-300 font-bold animate-slide-up block mb-5">
              A Movement for Authentic Living
            </span>
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-semibold text-white leading-[1.05] tracking-tight mb-6 animate-slide-up">
              We grow in the hard places —<br />
              <span className="italic text-amber-200">and bloom anyway.</span>
            </h1>
            <p className="text-lg md:text-xl text-stone-200 leading-relaxed mb-8 max-w-xl animate-slide-up">
              Welcome to the Dandelion Farm: House of Bloom. 365 days of guided self-discovery — 
              shadow work, ancestral healing, AI-powered insight — and every membership plants a 
              seed toward a real, physical sanctuary.
            </p>
            
            {founderSpots > 0 && founderSpots <= 100 && (
              <div className="animate-slide-up mb-8">
                <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-md border border-amber-300/30 text-amber-200 px-5 py-2.5 rounded-full">
                  <Flame className="w-5 h-5 text-amber-400" />
                  <span className="text-sm"><strong className="font-bold text-white">{founderSpots}</strong> Founder spots left • 25% off for life + Numbered Badge</span>
                </div>
              </div>
            )}
            
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 animate-slide-up mb-10">
              <Button
                onClick={handleGetStarted}
                className="bg-amber-500 hover:bg-amber-400 text-stone-950 font-bold rounded-full text-lg px-10 py-6 shadow-xl hover:shadow-2xl transition-all"
                data-testid="hero-cta-btn"
              >
                Start Your 3-Day Free Trial
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <p className="text-sm text-stone-300">No credit card required to explore</p>
            </div>

            {/* Social proof */}
            <div className="flex flex-wrap items-center gap-5 text-stone-300 animate-slide-up">
              <div className="flex items-center gap-2">
                <div className="flex -space-x-2">
                  {['🌸', '🌿', '💜', '🌻', '✨'].map((emoji) => (
                    <div key={emoji} className="w-8 h-8 rounded-full bg-white/15 backdrop-blur border border-white/20 flex items-center justify-center text-sm">
                      {emoji}
                    </div>
                  ))}
                </div>
                <span className="text-sm">Hundreds finding their way home</span>
              </div>
              <div className="flex items-center gap-1">
                {[1,2,3,4,5].map(i => (
                  <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                ))}
                <span className="text-sm ml-1">4.9/5 rating</span>
              </div>
            </div>
          </div>
        </div>

        <a href="#movement" className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/60 hover:text-white transition-colors animate-float">
          <ChevronDown className="w-8 h-8" />
        </a>
      </section>

      {/* ============ SECTION 2: THE MOVEMENT ============ */}
      <section id="movement" className="py-24 md:py-32 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Mission split */}
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center mb-24">
            <div className="relative">
              <img 
                src={FARMHOUSE_IMG} 
                alt="The House of Bloom — a warm farmhouse sanctuary" 
                className="rounded-3xl w-full h-[420px] object-cover shadow-xl"
              />
              <div className="absolute -bottom-6 -right-4 md:right-8 bg-white rounded-2xl shadow-lg border border-stone-200/80 px-5 py-4 max-w-[240px]">
                <p className="font-heading text-lg font-semibold text-foreground leading-snug">The House of Bloom</p>
                <p className="text-xs text-muted-foreground mt-1">A physical sanctuary, built by this community — one membership at a time.</p>
              </div>
            </div>
            <div>
              <span className="overline-label block mb-4">The Movement</span>
              <h2 className="font-heading text-4xl md:text-5xl font-semibold text-foreground tracking-tight mb-6">
                More than an app.<br />A sanctuary in the making.
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed mb-8">
                Like the dandelion, we take root where life is hardest and open for the sun anyway. 
                Every practice you complete grows your own Self Garden — and helps build the real-world 
                House of Bloom sanctuary for women rebuilding their lives.
              </p>
              <div className="space-y-4">
                {[
                  { icon: Leaf, title: "Practice daily", desc: "Meditations, breathwork, journaling and mood check-ins — small anchors, every day." },
                  { icon: Sparkles, title: "Grow your garden", desc: "Watch your Self Garden bloom in real-time as you show up for yourself." },
                  { icon: Users, title: "Build the House of Bloom", desc: "Your membership directly funds the physical sanctuary. You're part of the mission." },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-primary" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="font-semibold text-foreground">{item.title}</p>
                      <p className="text-sm text-muted-foreground">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Jules letter — condensed & expandable */}
          <Card className="rounded-3xl border border-stone-200/80 bg-gradient-to-br from-teal-50/60 via-white to-amber-50/40 mb-24 overflow-hidden" data-testid="jules-welcome-card">
            <CardContent className="p-8 md:p-12">
              <div className="max-w-3xl mx-auto">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-500/20 to-amber-500/20 flex items-center justify-center">
                    <Quote className="w-6 h-6 text-primary" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="font-heading text-xl font-semibold text-foreground">A letter from Jules</p>
                    <p className="text-sm text-primary">Founder of the movement</p>
                  </div>
                </div>

                <p className="font-heading text-2xl md:text-3xl text-foreground italic leading-relaxed mb-4">
                  "Welcome home. I know what it's like to finally be safe but still feel like a stranger in your own skin. 
                  You've spent a long time just trying to survive. Now, we're going to practice staying."
                </p>

                {showFullLetter && (
                  <div className="space-y-4 text-foreground/85 leading-relaxed animate-fade-in mb-4">
                    <p>
                      This is your space to anchor who you are. Every day. I've put the systems and tools in here that kept me standing when the ground felt shaky. These are the same frameworks I used to rebuild my own life after the fires.
                    </p>
                    <p>
                      Think of this app as your digital sanctuary. Like the dandelion, we are kindred. We grow in the hard places, and we open up for the sun together.
                    </p>
                    <p>
                      You'll notice a garden here, too. It's not just for show. Every time you check in and use a tool, you're planting something. It's a way to see the ground you're gaining in real-time.
                    </p>
                    <p>
                      Take a look around. Don't feel like you have to do everything at once. Start small. Pick one tool, one tracker, or one audio that feels right for right now.
                    </p>
                    <p>
                      Every time you use this app, you're helping us build the physical House of Bloom sanctuary, too. You're part of the mission now.
                    </p>
                    <p className="font-heading text-lg font-semibold text-foreground">
                      I'm so glad you're here. Let's find your feet together.
                    </p>
                  </div>
                )}

                <button
                  onClick={() => setShowFullLetter(!showFullLetter)}
                  className="text-primary text-sm font-medium hover:text-primary/80 flex items-center gap-1"
                  data-testid="jules-letter-toggle"
                >
                  {showFullLetter ? "Show less" : "Read the full letter"}
                  <ChevronDown className={`w-4 h-4 transition-transform ${showFullLetter ? 'rotate-180' : ''}`} />
                </button>
              </div>
            </CardContent>
          </Card>

          {/* Pillars header */}
          <div className="max-w-2xl mb-12">
            <span className="overline-label block mb-4">What's Inside</span>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-foreground tracking-tight mb-4">
              Everything you need for deep transformation
            </h2>
            <p className="text-muted-foreground text-lg">
              76 lessons across 4 transformational modules — Shadow Self (22), Emotional Intelligence (22), 
              Healthy Habits (20), Ancestral Wisdom (12) — plus the daily tools that keep you anchored.
            </p>
          </div>

          {/* Feature bento */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
            {features.map((f) => (
              <div key={f.testid} className="feature-card !p-6" data-testid={f.testid}>
                <div className={`w-11 h-11 rounded-2xl ${f.bg} flex items-center justify-center mb-4`}>
                  <f.icon className={`w-5 h-5 ${f.color}`} strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-xl font-semibold mb-2">{f.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>

          {/* Witness circle + meditation strip */}
          <div className="grid lg:grid-cols-5 gap-5">
            <Card className="lg:col-span-3 rounded-3xl border border-stone-200/80 bg-white overflow-hidden">
              <CardContent className="p-8 md:p-10">
                <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-3 py-1 rounded-full text-sm mb-4">
                  <Users className="w-4 h-4" />
                  Community
                </div>
                <h3 className="font-heading text-3xl font-semibold text-foreground mb-4">
                  Witness Circle: soul-to-soul accountability
                </h3>
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  Get matched with an anonymous soul companion who understands your journey. 
                  Weekly reflections, shared wins, held space — no personal details, 
                  just two souls walking alongside each other.
                </p>
                <div className="flex flex-wrap gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-emerald-500" />
                    <span>100% Anonymous</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MessageCircle className="w-4 h-4 text-teal-500" />
                    <span>Weekly Check-ins</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Heart className="w-4 h-4 text-amber-500" />
                    <span>Soul Name Matching</span>
                  </div>
                </div>
              </CardContent>
            </Card>
            <div 
              className="lg:col-span-2 rounded-3xl overflow-hidden relative min-h-[260px]"
            >
              <img src={MEDITATION_IMG} alt="A woman meditating at sunset" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              <div className="absolute bottom-0 left-0 p-8">
                <p className="font-heading text-2xl text-white italic leading-snug">
                  "The journey back to yourself is the most sacred pilgrimage you'll ever take."
                </p>
                <span className="font-handwritten text-lg text-amber-300">— Source</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ SECTION 3: JOIN THE MOVEMENT ============ */}
      <section className="py-24 md:py-32 px-6 bg-gradient-to-b from-stone-100/60 to-background border-t border-stone-200/60">
        <div className="max-w-7xl mx-auto">
          {/* Testimonials */}
          <div className="mb-24">
            <div className="max-w-2xl mb-10">
              <span className="overline-label block mb-4">Voices from the Journey</span>
              <h2 className="font-heading text-4xl md:text-5xl font-semibold text-foreground tracking-tight">
                Real stories from the movement
              </h2>
            </div>

            <div className="relative overflow-hidden" data-testid="testimonial-carousel">
              <div className="flex gap-6 animate-scroll-carousel">
                {[...(userReviews.length > 0 ? userReviews : []), ...testimonials.map((t, i) => ({
                  id: `static-${i}`,
                  rating: 5,
                  content: t.quote,
                  display_name: t.name,
                  title: t.location
                }))].map((review) => (
                  <Card key={review.id || review.content} className="bg-white rounded-3xl border border-stone-200/80 min-w-[320px] md:min-w-[380px] flex-shrink-0">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-1 mb-4">
                        {[1,2,3,4,5].map(j => (
                          <Star key={j} className={`w-4 h-4 ${j <= (review.rating || 5) ? 'fill-amber-400 text-amber-400' : 'text-gray-200'}`} />
                        ))}
                      </div>
                      <p className="text-foreground mb-6 leading-relaxed line-clamp-4 font-heading text-lg italic">
                        "{review.content}"
                      </p>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500/20 to-amber-500/20 flex items-center justify-center">
                          <Heart className="w-5 h-5 text-primary" strokeWidth={1.5} />
                        </div>
                        <div>
                          <p className="font-medium text-foreground">{review.display_name}</p>
                          {review.title && <p className="text-xs text-muted-foreground">{review.title}</p>}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className="mb-24">
            <div className="text-center mb-12">
              <span className="overline-label block mb-4">Join the Movement</span>
              <h2 className="font-heading text-4xl md:text-5xl font-semibold text-foreground tracking-tight mb-4">
                Start free, go deeper when ready
              </h2>
              <p className="text-muted-foreground text-lg">
                A generous free tier to begin. Premium unlocks the full experience — and builds the House of Bloom.
              </p>
              <div className="mt-4 flex flex-col sm:flex-row items-center justify-center gap-3">
                <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm">
                  <Award className="w-4 h-4" />
                  First 100 paid subscribers get 25% off for life + Founder Badge
                </div>
                <div className="inline-flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-full text-sm shadow-md" data-testid="price-drop-banner">
                  <Sparkles className="w-4 h-4" />
                  <span>Price drop: was <s className="opacity-70">$15.55</s>/mo — now <strong>$5.55</strong>. More accessible than ever.</span>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-4 gap-5">
              {/* Free */}
              <Card className="bg-white rounded-3xl border border-stone-200/80">
                <CardContent className="p-6">
                  <h3 className="font-heading text-xl font-semibold mb-2">Free Forever</h3>
                  <p className="text-3xl font-bold text-foreground mb-4 font-body">$0</p>
                  <ul className="space-y-2 mb-6">
                    {[
                      "State of Being Quiz",
                      "Daily mantras & blessings",
                      "365 journal prompts",
                      "Mood tracker & streaks",
                      "Loving kindness practice",
                      "7 Shadow Self sessions",
                      "5 Ancestral Wisdom sessions",
                      "4 free mood playlists"
                    ].map((item) => (
                      <li key={item} className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Check className="w-3 h-3 text-green-500 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                  <Button variant="outline" className="w-full text-sm rounded-full" onClick={handleGetStarted}>
                    Get Started Free
                  </Button>
                </CardContent>
              </Card>

              {/* Monthly */}
              <Card className="bg-white rounded-3xl border-2 border-primary relative shadow-lg">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-primary text-white text-xs px-3 py-1 rounded-full">
                    Most Popular
                  </span>
                </div>
                <CardContent className="p-6">
                  <h3 className="font-heading text-xl font-semibold mb-2">Monthly</h3>
                  <p className="text-3xl font-bold text-foreground mb-1 font-body">
                    $5.55<span className="text-sm font-normal text-muted-foreground">/mo</span>
                    <span className="text-sm font-normal text-muted-foreground line-through ml-2">$15.55</span>
                  </p>
                  <p className="text-xs text-muted-foreground mb-4">USD • 3-day free trial</p>
                  <ul className="space-y-2 mb-6">
                    {[
                      "Everything in Free",
                      "Spirit AI unlimited",
                      "Pattern Recognition AI",
                      "All 76 learning modules",
                      "All 9 mood playlists",
                      "Priority support"
                    ].map((item) => (
                      <li key={item} className="flex items-center gap-2 text-xs text-foreground">
                        <Check className="w-3 h-3 text-primary flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                  <Button className="btn-primary w-full text-sm" onClick={handleGetStarted}>
                    Start Free Trial
                  </Button>
                </CardContent>
              </Card>

              {/* Annual */}
              <Card className="bg-white rounded-3xl border border-stone-200/80">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-heading text-xl font-semibold">Annual</h3>
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                      2 months free
                    </span>
                  </div>
                  <p className="text-3xl font-bold text-foreground mb-1 font-body">
                    $55.55<span className="text-sm font-normal text-muted-foreground">/yr</span>
                    <span className="text-sm font-normal text-muted-foreground line-through ml-2">$99.99</span>
                  </p>
                  <p className="text-xs text-muted-foreground mb-4">USD • Just $4.63/month</p>
                  <ul className="space-y-2 mb-6">
                    {[
                      "Everything in Monthly",
                      "Best value for committed seekers",
                      "Lock in your rate",
                      "Annual content updates"
                    ].map((item) => (
                      <li key={item} className="flex items-center gap-2 text-xs text-foreground">
                        <Check className="w-3 h-3 text-green-600 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                  <Button className="w-full text-sm bg-green-600 hover:bg-green-700 text-white rounded-full" onClick={handleGetStarted}>
                    Get Annual
                  </Button>
                </CardContent>
              </Card>

              {/* Lifetime */}
              <Card className="bg-white rounded-3xl border border-amber-200">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-heading text-xl font-semibold">Lifetime</h3>
                    <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                      Best Deal
                    </span>
                  </div>
                  <p className="text-3xl font-bold text-foreground mb-1 font-body">
                    $111
                    <span className="text-sm font-normal text-muted-foreground line-through ml-2">$222.22</span>
                  </p>
                  <p className="text-xs text-muted-foreground mb-4">USD • One-time payment</p>
                  <ul className="space-y-2 mb-6">
                    {[
                      "Everything, forever",
                      "All future features included",
                      "Never pay again",
                      "Support our mission"
                    ].map((item) => (
                      <li key={item} className="flex items-center gap-2 text-xs text-foreground">
                        <Check className="w-3 h-3 text-amber-600 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                  <Button className="w-full text-sm bg-amber-600 hover:bg-amber-700 text-white rounded-full" onClick={handleGetStarted}>
                    Get Lifetime
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* FAQ */}
          <div className="max-w-3xl mx-auto mb-24">
            <div className="text-center mb-10">
              <h2 className="font-heading text-4xl font-semibold text-foreground tracking-tight">
                Questions? We've got answers
              </h2>
            </div>

            <div className="space-y-3">
              {faqs.map((faq, i) => (
                <Card key={faq.q} className="bg-white rounded-2xl border border-stone-200/80 overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full p-5 text-left flex items-center justify-between gap-4"
                    data-testid={`faq-toggle-${i}`}
                  >
                    <span className="font-medium text-foreground">{faq.q}</span>
                    <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                  </button>
                  {openFaq === i && (
                    <div className="px-5 pb-5">
                      <p className="text-muted-foreground">{faq.a}</p>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          </div>

          {/* Final CTA */}
          <div className="relative rounded-3xl overflow-hidden">
            <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/60" />
            <div className="relative z-10 p-10 md:p-16 text-center max-w-3xl mx-auto">
              <h2 className="font-heading text-4xl md:text-5xl font-semibold text-white tracking-tight mb-5">
                Ready to grow with us?
              </h2>
              <p className="text-lg text-stone-200 mb-6 leading-relaxed">
                The journey home to yourself begins with a single step. The movement — 
                and the House of Bloom — grows with every soul that joins.
              </p>
              
              {founderSpots > 0 && founderSpots <= 100 && (
                <p className="text-sm text-amber-300 mb-6">
                  <strong>{founderSpots} founder spots remaining.</strong> 25% off for life + your numbered Founder Badge.
                </p>
              )}
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button
                  onClick={handleGetStarted}
                  className="bg-amber-500 hover:bg-amber-400 text-stone-950 font-bold rounded-full text-lg px-10 py-6 shadow-xl"
                  data-testid="cta-btn"
                >
                  Start Your Free Trial
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
              
              <p className="text-sm text-stone-300 mt-6">
                💳 No credit card required • Cancel anytime • 24/7 crisis support included
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 px-6 border-t border-stone-200/60 bg-background">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-10 mb-12">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Heart className="w-5 h-5 text-primary" strokeWidth={1.5} />
                <span className="font-heading text-xl font-semibold">Authentic Self 365</span>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Dandelion Farm: House of Bloom — a movement for authentic living. 
                We grow in the hard places, and we open up for the sun together.
              </p>
            </div>
            <div className="md:col-span-2">
              <h3 className="font-heading text-lg font-semibold text-foreground mb-2">
                About the Founder
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                <strong className="text-foreground">Julie (J.C. Wallace)</strong> is a lived-experience advocate, author, and the heart behind JVDH Lived Insights. Stepping proudly into her ninth year of recovery, she created this movement to help individuals reclaim their inner voice and live with radical self-awareness.
              </p>
              <p className="text-sm text-muted-foreground italic mt-2 font-heading text-base">
                "This is the sanctuary I built to help you navigate your own journey of rising." — Ms J. C. Wallace
              </p>
            </div>
          </div>
          
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-6 border-t border-stone-200/60">
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} Authentic Self 365 · Created by <strong className="text-foreground">Ms J. C. Wallace</strong> & <strong className="text-foreground">JVDH Lived Insights</strong>
            </p>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <a href="https://intentionalinsights.gumroad.com/" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">
                JVDH Intentional Insights
              </a>
              <span>•</span>
              <span>Made with love in Australia 🇦🇺</span>
            </div>
          </div>
          <p className="text-xs text-muted-foreground/70 mt-4 text-center">
            If you're in crisis, please reach out to Lifeline (13 11 14) or Beyond Blue (1300 22 4636).
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
